import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  GripVertical, ArrowUp, ArrowDown, Eye, EyeOff, Save, RotateCcw,
  ShieldCheck, Store, ListOrdered,
} from "lucide-react";
import { ADMIN_MENU } from "./AdminLayout";
import { VENDOR_MENU } from "../vendor/VendorLayout";

const BASE = { admin: ADMIN_MENU, vendor: VENDOR_MENU };
// Toujours accessible : on interdit de masquer la page de gestion des menus elle-même.
const NEVER_HIDE = new Set(["/admin/menus"]);

function orderItems(base, layout) {
  const byKey = new Map(base.map((i) => [i.to, i]));
  const ordered = [];
  (layout?.order || []).forEach((k) => {
    if (byKey.has(k)) {
      ordered.push(byKey.get(k));
      byKey.delete(k);
    }
  });
  base.forEach((i) => {
    if (byKey.has(i.to)) ordered.push(i);
  });
  return ordered;
}

export default function AdminMenus() {
  const [scope, setScope] = useState("admin");
  const [layouts, setLayouts] = useState({ admin: { order: [], hidden: [] }, vendor: { order: [], hidden: [] } });
  const [items, setItems] = useState([]);
  const [hidden, setHidden] = useState(new Set());
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    api.get("/admin/menu-layout").then(({ data }) => setLayouts(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const lay = layouts[scope] || { order: [], hidden: [] };
    setItems(orderItems(BASE[scope], lay));
    setHidden(new Set(lay.hidden || []));
    setDirty(false);
  }, [scope, layouts]);

  const move = (i, dir) => {
    setItems((prev) => {
      const a = [...prev];
      const j = i + dir;
      if (j < 0 || j >= a.length) return a;
      [a[i], a[j]] = [a[j], a[i]];
      return a;
    });
    setDirty(true);
  };

  const onDrop = (i) => {
    setItems((prev) => {
      if (dragIdx === null || dragIdx === i) return prev;
      const a = [...prev];
      const [m] = a.splice(dragIdx, 1);
      a.splice(i, 0, m);
      return a;
    });
    setDragIdx(null);
    setOverIdx(null);
    setDirty(true);
  };

  const toggleHide = (to) => {
    if (NEVER_HIDE.has(to)) return;
    setHidden((prev) => {
      const n = new Set(prev);
      if (n.has(to)) n.delete(to);
      else n.add(to);
      return n;
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const order = items.map((i) => i.to);
      const { data } = await api.put("/admin/menu-layout", { scope, order, hidden: [...hidden] });
      setLayouts((prev) => ({ ...prev, [scope]: data }));
      setDirty(false);
      toast.success("Menu enregistré — le nouvel ordre s'applique immédiatement.");
    } catch {
      toast.error("Échec de l'enregistrement du menu.");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Réinitialiser ce menu à l'ordre par défaut ? Vos positions personnalisées seront perdues.")) return;
    setSaving(true);
    try {
      const { data } = await api.put("/admin/menu-layout", { scope, order: [], hidden: [] });
      setLayouts((prev) => ({ ...prev, [scope]: data }));
      toast.success("Menu réinitialisé à l'ordre par défaut.");
    } catch {
      toast.error("Échec de la réinitialisation.");
    } finally {
      setSaving(false);
    }
  };

  const hiddenCount = useMemo(() => items.filter((i) => hidden.has(i.to)).length, [items, hidden]);

  const tabs = [
    { id: "admin", label: "Menu Admin", icon: ShieldCheck },
    { id: "vendor", label: "Menu Revendeur", icon: Store },
  ];

  return (
    <div className="space-y-5" data-testid="admin-menus-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-xl bg-ink text-white">
            <ListOrdered className="h-5 w-5" />
          </span>
          <div>
            <h1 className="font-display text-2xl font-extrabold text-ink">Positions des menus</h1>
            <p className="mt-0.5 text-sm text-slate-500">
              Glissez-déposez ou utilisez les flèches pour réordonner les entrées du menu latéral.
              Masquez celles que vous ne souhaitez pas afficher. Valable pour l'espace admin et l'espace revendeur.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="menus-reset"
            onClick={reset}
            disabled={saving}
            className="flex items-center gap-1.5 rounded-full border border-line bg-white px-4 py-2 text-xs font-bold text-slate-600 transition-colors hover:border-slate-400 hover:text-ink disabled:opacity-50"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Réinitialiser
          </button>
          <button
            data-testid="menus-save"
            onClick={save}
            disabled={saving || !dirty}
            className="flex items-center gap-1.5 rounded-full bg-brand px-5 py-2 text-xs font-bold text-white transition-transform hover:scale-[1.03] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Save className="h-3.5 w-3.5" /> {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </div>

      <div className="flex gap-2" data-testid="menus-tabs">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            data-testid={`menus-tab-${id}`}
            onClick={() => setScope(id)}
            className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-colors ${
              scope === id ? "bg-ink text-white" : "border border-line bg-white text-slate-600 hover:text-ink"
            }`}
          >
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      <div className="rounded-2xl border border-line bg-white p-3 sm:p-4">
        <div className="mb-3 flex items-center justify-between px-1">
          <p className="text-xs font-semibold text-slate-500">
            {items.length} entrée(s){hiddenCount > 0 ? ` · ${hiddenCount} masquée(s)` : ""}
          </p>
          {dirty && (
            <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-bold text-amber-700" data-testid="menus-dirty">
              Modifications non enregistrées
            </span>
          )}
        </div>
        <ul className="space-y-1.5" data-testid={`menus-list-${scope}`}>
          {items.map((item, i) => {
            const Icon = item.icon;
            const isHidden = hidden.has(item.to);
            const locked = NEVER_HIDE.has(item.to);
            return (
              <li
                key={item.to}
                data-testid={`menu-row-${item.testId || i}`}
                draggable
                onDragStart={() => setDragIdx(i)}
                onDragOver={(e) => { e.preventDefault(); setOverIdx(i); }}
                onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
                onDrop={() => onDrop(i)}
                className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 transition-colors ${
                  overIdx === i && dragIdx !== null ? "border-brand bg-brand-light" : "border-line bg-surface"
                } ${isHidden ? "opacity-55" : ""} ${dragIdx === i ? "ring-2 ring-brand" : ""}`}
              >
                <span className="cursor-grab text-slate-400 active:cursor-grabbing" title="Glisser pour déplacer">
                  <GripVertical className="h-5 w-5" />
                </span>
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-ink ring-1 ring-line">
                  <Icon className="h-4 w-4" />
                </span>
                <span className="flex-1 truncate text-sm font-semibold text-ink">{item.label}</span>

                <span className="hidden text-[11px] font-semibold text-slate-400 sm:block">#{i + 1}</span>

                <div className="flex items-center gap-1">
                  <button
                    data-testid={`menu-up-${item.testId || i}`}
                    onClick={() => move(i, -1)}
                    disabled={i === 0}
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-white hover:text-ink disabled:opacity-30"
                    title="Monter"
                  >
                    <ArrowUp className="h-4 w-4" />
                  </button>
                  <button
                    data-testid={`menu-down-${item.testId || i}`}
                    onClick={() => move(i, 1)}
                    disabled={i === items.length - 1}
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-white hover:text-ink disabled:opacity-30"
                    title="Descendre"
                  >
                    <ArrowDown className="h-4 w-4" />
                  </button>
                  <button
                    data-testid={`menu-toggle-${item.testId || i}`}
                    onClick={() => toggleHide(item.to)}
                    disabled={locked}
                    className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
                      locked
                        ? "text-slate-300"
                        : isHidden
                        ? "text-slate-400 hover:bg-white hover:text-ink"
                        : "text-emerald-600 hover:bg-white"
                    }`}
                    title={locked ? "Toujours visible" : isHidden ? "Afficher" : "Masquer"}
                  >
                    {isHidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
